import type { OrderWarrantyClaimDetail } from './types';

/** One Activity Timeline entry in the **drawer's** vocabulary. Deliberately not the wire shape: the
 *  backend sends `{ status, timestamp }` and this is `{ status, at }`, so the derived events and the
 *  served ones stay one type and `<Timeline>` needs no branching. `status` is a bare `string` because
 *  BE types it that way too — a token they add later reaches the drawer (which degrades it to a
 *  neutral chip) instead of failing the parse. */
export type OrderWarrantyTimelineEvent = { status: string; at: string };

/**
 * Prefer-served-else-derive (ADR-0023 delta 5, grilling Q7). BE added the served field on 112445
 * (2026-08-04) as **`timeline: [{ status, timestamp }]`** — not the `activity: [{ status, at }]` this
 * originally guessed at, which is why the prefer-served branch silently never fired until now: the
 * field is `nullish`, so the wrong key read as "absent" and every claim fell through to the derived
 * path. Mapping the two keys here keeps the wire vocabulary out of the drawer.
 *
 * The derive path is unchanged and still needed — a deployment predating the field has no timeline.
 * That is the one structural difference from `orders/replacements/timeline.ts`, which derives
 * unconditionally because *no* replacement endpoint carries events.
 *
 * `Created`/`Pending` share `requestedAt` (one event, stamped twice); the terminal entry lands at
 * `resolvedAt` and is dropped when null rather than rendered as an invalid date. `status` is
 * already the terminal token, so no label mapping happens here.
 */
export function warrantyTimelineEvents(
  detail: Pick<OrderWarrantyClaimDetail, 'status' | 'requestedAt' | 'resolvedAt' | 'timeline'>,
): OrderWarrantyTimelineEvent[] {
  if (detail.timeline?.length) {
    return detail.timeline.map(({ status, timestamp }) => ({ status, at: timestamp }));
  }

  const events: OrderWarrantyTimelineEvent[] = [
    { status: 'created', at: detail.requestedAt },
    { status: 'pending', at: detail.requestedAt },
  ];
  if (detail.status !== 'pending' && detail.resolvedAt) {
    events.push({ status: detail.status, at: detail.resolvedAt });
  }
  return events;
}
